import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Save, ArrowLeft, Plus, Trash2, Settings, Eye, GripVertical, Loader2,
  Type, Mail, Phone, AlignLeft, ChevronDown, Circle, CheckSquare, Calendar,
  Hash, EyeOff, RefreshCw, Code, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_ICONS = {
  text: Type,
  email: Mail,
  phone: Phone,
  textarea: AlignLeft,
  select: ChevronDown,
  radio: Circle,
  checkbox: CheckSquare,
  date: Calendar,
  number: Hash,
  hidden: EyeOff
};

const FormBuilder = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const { token } = useAuth();
  
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldTypes, setFieldTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form settings
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [thankYouMessage, setThankYouMessage] = useState('Thank you for your submission!');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [notifyEmails, setNotifyEmails] = useState('');
  const [tagsToAdd, setTagsToAdd] = useState('');
  
  // Dialogs
  const [showAddField, setShowAddField] = useState(false);
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');

  // Fetch field types
  useEffect(() => {
    const fetchFieldTypes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/marketing/forms/field-types`);
        const data = await res.json();
        if (data.success) {
          setFieldTypes(data.field_types);
        }
      } catch (error) {
        console.error('Error fetching field types:', error);
      }
    };
    fetchFieldTypes();
  }, []);

  // Fetch form if editing
  useEffect(() => {
    const fetchForm = async () => {
      if (!formId || formId === 'new') {
        // Add default fields for new form
        setFields([
          { id: `field-${Date.now()}`, type: 'text', label: 'Full Name', placeholder: 'Enter your name', required: true, order: 0 },
          { id: `field-${Date.now() + 1}`, type: 'email', label: 'Email', placeholder: 'Enter your email', required: true, order: 1 }
        ]);
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/marketing/forms/${formId}?token=${token}`);
        const data = await res.json();
        
        if (data.success) {
          setForm(data.form);
          setFormName(data.form.name);
          setFormDescription(data.form.description || '');
          setThankYouMessage(data.form.thank_you_message || 'Thank you!');
          setRedirectUrl(data.form.redirect_url || '');
          setNotifyEmails((data.form.notify_emails || []).join(', '));
          setTagsToAdd((data.form.tags_to_add || []).join(', '));
          setFields(data.form.fields || []);
        }
      } catch (error) {
        console.error('Error fetching form:', error);
        toast.error('Failed to load form');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [formId, token]);

  const handleAddField = (fieldType) => {
    const newField = {
      id: `field-${Date.now()}`,
      type: fieldType.type,
      label: fieldType.label,
      placeholder: '',
      required: false,
      options: fieldType.type === 'select' || fieldType.type === 'radio' || fieldType.type === 'checkbox' 
        ? ['Option 1', 'Option 2'] 
        : null,
      order: fields.length
    };
    
    setFields([...fields, newField]);
    setShowAddField(false);
    toast.success(`Added ${fieldType.label} field`);
  };

  const handleUpdateField = (fieldId, updates) => {
    setFields(fields.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  };

  const handleDeleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    setShowFieldConfig(false);
    setSelectedField(null);
    toast.success('Field deleted');
  };

  const handleSaveForm = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    if (fields.length === 0) {
      toast.error('Please add at least one field');
      return;
    }

    setSaving(true);
    
    try {
      const payload = {
        name: formName,
        description: formDescription,
        fields: fields.map((f, idx) => ({ ...f, order: idx })),
        thank_you_message: thankYouMessage,
        redirect_url: redirectUrl || null,
        notify_emails: notifyEmails ? notifyEmails.split(',').map(e => e.trim()).filter(Boolean) : [],
        tags_to_add: tagsToAdd ? tagsToAdd.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      let res;
      if (formId && formId !== 'new') {
        res = await fetch(`${API_URL}/api/marketing/forms/${formId}?token=${token}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_URL}/api/marketing/forms?token=${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const data = await res.json();

      if (data.success) {
        toast.success('Form saved successfully');
        if (!formId || formId === 'new') {
          navigate(`/marketing/forms/${data.form.id}`);
        }
        setForm(data.form);
      } else {
        toast.error(data.detail || 'Failed to save form');
      }
    } catch (error) {
      toast.error('Error saving form');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formId || formId === 'new') {
      toast.error('Save the form first');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/marketing/forms/${formId}/publish?token=${token}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Form published');
        setForm(prev => ({ ...prev, status: 'active' }));
      } else {
        toast.error(data.detail || 'Failed to publish form');
      }
    } catch (error) {
      toast.error('Error publishing form');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" data-testid="form-builder">
      {/* Header */}
      <div className="h-16 border-b bg-white dark:bg-slate-900 px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/forms')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Form Name"
              className="border-0 text-lg font-semibold focus-visible:ring-0 p-0 h-auto"
            />
          </div>
          {form?.status && (
            <Badge variant={form.status === 'active' ? 'success' : 'secondary'}>
              {form.status}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          {form?.status === 'active' && (
            <Button variant="outline" size="sm" onClick={() => window.open(`${API_URL}/api/marketing/forms/${formId}/embed`, '_blank')}>
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSaveForm} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          {form?.status !== 'active' && (
            <Button size="sm" onClick={handlePublish} disabled={!formId || formId === 'new'}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Field List */}
        <div className="w-64 border-r bg-slate-50 dark:bg-slate-800 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Fields</h3>
            <Button size="sm" variant="outline" onClick={() => setShowAddField(true)}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="space-y-2">
              {fields.map((field, index) => {
                const Icon = FIELD_ICONS[field.type] || Type;
                return (
                  <div
                    key={field.id}
                    onClick={() => {
                      setSelectedField(field);
                      setShowFieldConfig(true);
                    }}
                    className="flex items-center gap-2 p-2 bg-white dark:bg-slate-700 rounded-lg border cursor-pointer hover:border-accent transition-colors"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400" />
                    <Icon className="w-4 h-4 text-slate-500" />
                    <span className="flex-1 text-sm truncate">{field.label}</span>
                    {field.required && (
                      <span className="text-red-500 text-xs">*</span>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Center Panel - Form Preview */}
        <div className="flex-1 p-8 overflow-auto bg-slate-100 dark:bg-slate-900">
          <div className="max-w-md mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-2">{formName || 'Untitled Form'}</h2>
            {formDescription && (
              <p className="text-slate-500 mb-6">{formDescription}</p>
            )}

            <div className="space-y-4">
              {fields.map((field) => (
                <div key={field.id} className="space-y-1">
                  <label className="text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {field.type === 'textarea' ? (
                    <Textarea placeholder={field.placeholder} disabled className="bg-slate-50" />
                  ) : field.type === 'select' ? (
                    <Select disabled>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder={field.placeholder || 'Select...'} />
                      </SelectTrigger>
                    </Select>
                  ) : field.type === 'radio' ? (
                    <div className="space-y-2">
                      {(field.options || []).map((opt, i) => (
                        <label key={i} className="flex items-center gap-2 text-sm">
                          <input type="radio" name={field.id} disabled />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <div className="space-y-2">
                      {(field.options || []).map((opt, i) => (
                        <label key={i} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" disabled />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : field.type === 'hidden' ? (
                    <p className="text-xs text-slate-400 italic">[Hidden field]</p>
                  ) : (
                    <Input 
                      type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      placeholder={field.placeholder} 
                      disabled 
                      className="bg-slate-50"
                    />
                  )}
                </div>
              ))}
            </div>

            <Button className="w-full mt-6" disabled>
              Submit
            </Button>
          </div>
        </div>
      </div>

      {/* Add Field Dialog */}
      <Dialog open={showAddField} onOpenChange={setShowAddField}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
            <DialogDescription>Select a field type to add to your form</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2">
            {fieldTypes.map((fieldType) => {
              const Icon = FIELD_ICONS[fieldType.type] || Type;
              return (
                <button
                  key={fieldType.type}
                  onClick={() => handleAddField(fieldType)}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:border-accent hover:bg-accent/5 transition-colors text-left"
                >
                  <Icon className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-sm">{fieldType.label}</p>
                    <p className="text-xs text-slate-500">{fieldType.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Field Configuration Dialog */}
      <Dialog open={showFieldConfig} onOpenChange={setShowFieldConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Field</DialogTitle>
          </DialogHeader>

          {selectedField && (
            <div className="space-y-4">
              <div>
                <Label>Label</Label>
                <Input
                  value={selectedField.label}
                  onChange={(e) => {
                    handleUpdateField(selectedField.id, { label: e.target.value });
                    setSelectedField({ ...selectedField, label: e.target.value });
                  }}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Placeholder</Label>
                <Input
                  value={selectedField.placeholder || ''}
                  onChange={(e) => {
                    handleUpdateField(selectedField.id, { placeholder: e.target.value });
                    setSelectedField({ ...selectedField, placeholder: e.target.value });
                  }}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Required</Label>
                <Switch
                  checked={selectedField.required}
                  onCheckedChange={(checked) => {
                    handleUpdateField(selectedField.id, { required: checked });
                    setSelectedField({ ...selectedField, required: checked });
                  }}
                />
              </div>

              {(selectedField.type === 'select' || selectedField.type === 'radio' || selectedField.type === 'checkbox') && (
                <div>
                  <Label>Options (one per line)</Label>
                  <Textarea
                    value={(selectedField.options || []).join('\n')}
                    onChange={(e) => {
                      const options = e.target.value.split('\n').filter(Boolean);
                      handleUpdateField(selectedField.id, { options });
                      setSelectedField({ ...selectedField, options });
                    }}
                    className="mt-1"
                    rows={4}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="destructive" size="sm" onClick={() => handleDeleteField(selectedField?.id)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Field
            </Button>
            <Button onClick={() => setShowFieldConfig(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Form Settings</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Form Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Optional description shown above the form"
                className="mt-1"
              />
            </div>

            <Separator />

            <div>
              <Label>Thank You Message</Label>
              <Textarea
                value={thankYouMessage}
                onChange={(e) => setThankYouMessage(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Redirect URL (optional)</Label>
              <Input
                value={redirectUrl}
                onChange={(e) => setRedirectUrl(e.target.value)}
                placeholder="https://yoursite.com/thank-you"
                className="mt-1"
              />
            </div>

            <Separator />

            <div>
              <Label>Notification Emails (comma-separated)</Label>
              <Input
                value={notifyEmails}
                onChange={(e) => setNotifyEmails(e.target.value)}
                placeholder="sales@company.com, marketing@company.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Tags to Add (comma-separated)</Label>
              <Input
                value={tagsToAdd}
                onChange={(e) => setTagsToAdd(e.target.value)}
                placeholder="lead, website-form"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowSettings(false)}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormBuilder;
